import React from 'react';
import { Button, Tooltip, Space } from 'antd';
import { PlusOutlined, UndoOutlined } from '@ant-design/icons';
import type { HandsontableWrapperRef } from '../handsontable/HandsontableWrapperSimple';
import type { TrackedTableRef } from '../handsontable/TrackedTable';
import { useAddTableRow } from '../handsontable/hooks/useAddTableRow';
import { ExportButton } from './ExportButton';
import styles from '../../styles/components/LocalTableToolbar.module.scss';
import { useUIConfig } from '../../contexts/UIConfigContext';

export interface LocalTableToolbarProps {
  tableRef?: React.RefObject<HandsontableWrapperRef | TrackedTableRef | null>;
  exportFileName?: string;
  exportSheetName?: string;
  disabled?: boolean;
  variant?: 'default' | 'compact' | 'modern';
  showLabel?: boolean;
  /** Показывать кнопку «Добавить строку» */
  showAddRow?: boolean;
  /** Tooltip кнопки добавления строки */
  addRowTooltip?: string;
  /** Callback после добавления строки (получает индекс новой строки) */
  onRowAdded?: (rowIndex: number) => void;
  /** Сбросить пользовательские настройки колонок (ширины). Кнопка показывается только если передан callback. */
  onResetColumnSettings?: () => void;
  /** Дополнительный элемент (например, кнопка версии) */
  extra?: React.ReactNode;
  /** UI Config keys для цветов шапки в Excel-экспорте (см. ExportButton). */
  configSection?: string;
  configHeaderKey?: string;
  configNestedHeaderKey?: string;
  dynamicHeaderColors?: boolean;
}

/**
 * Локальный тулбар таблицы.
 *
 * Содержит:
 * - Кнопку экспорта в Excel
 * - Опциональную кнопку добавления строки (showAddRow)
 *
 * Кнопки действий (undo/redo/clear) и отображение изменённых ячеек
 * перенесены в FloatingActionBar через ToolbarContext.
 */
export const LocalTableToolbar: React.FC<LocalTableToolbarProps> = ({
  tableRef,
  exportFileName,
  exportSheetName,
  disabled = false,
  variant = 'default',
  showLabel = false,
  showAddRow = false,
  addRowTooltip,
  onRowAdded,
  onResetColumnSettings,
  extra,
  configSection,
  configHeaderKey,
  configNestedHeaderKey,
  dynamicHeaderColors,
}) => {
  const { getButtonText } = useUIConfig();
  const resolvedTooltip = addRowTooltip ?? getButtonText('add_row', 'Добавить строку');

  const handleAddRow = useAddTableRow(tableRef, onRowAdded);

  if (!tableRef) return null;

  const resetBtn = onResetColumnSettings ? (
    <Tooltip title="Сбросить настройки колонок" placement="bottom">
      <Button
        data-tour-id="local-toolbar-reset-widths"
        icon={<UndoOutlined />}
        onClick={onResetColumnSettings}
        disabled={disabled}
        size="small"
        type="text"
      />
    </Tooltip>
  ) : null;

  if (!showAddRow && !extra && !resetBtn) {
    return (
      <Space size={4} className={styles.toolbar} data-tour-id="local-table-toolbar">
        <ExportButton
          tableRef={tableRef}
          fileName={exportFileName}
          sheetName={exportSheetName}
          disabled={disabled}
          variant={variant}
          showLabel={showLabel}
          configSection={configSection}
          configHeaderKey={configHeaderKey}
          configNestedHeaderKey={configNestedHeaderKey}
          dynamicHeaderColors={dynamicHeaderColors}
        />
      </Space>
    );
  }

  return (
    <Space size={4} className={styles.toolbar} data-tour-id="local-table-toolbar">
      {extra}
      {showAddRow && (
        <Tooltip title={resolvedTooltip} placement="bottom">
          <Button
            icon={<PlusOutlined />}
            onClick={handleAddRow}
            disabled={disabled}
            size="small"
            type="text"
            className={styles.addRowBtn}
          >
            {showLabel && 'Добавить'}
          </Button>
        </Tooltip>
      )}
      {resetBtn}
      <ExportButton
        tableRef={tableRef}
        fileName={exportFileName}
        sheetName={exportSheetName}
        disabled={disabled}
        variant={variant}
        showLabel={showLabel}
      />
    </Space>
  );
};
